import { AutoField, FieldLabel } from "@measured/puck";
import { Box, Divider, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useGetPuck } from "@measured/puck";

import ColorPickerField from "src/components/Puck/config/fields/ColorPickerField";
import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";
import { BORDER_WIDTH_OPTIONS, RADIUS_OPTIONS, SPACING_OPTIONS } from "src/components/Puck/config/layoutTokens";
import SpacingSidesField, { SpacingSidesValue } from "src/components/Puck/config/fields/SpacingSidesField";

export type GridCellLayout = {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    padding?: SpacingSidesValue;
};

export type GridCellLayoutsValue = Record<string, GridCellLayout>;

type GridCellLayoutFieldProps = {
    value?: GridCellLayoutsValue;
    onChange: (value: GridCellLayoutsValue) => void;
    readOnly?: boolean;
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

export const GridCellLayoutField = (props: GridCellLayoutFieldProps) => {
    const { value, onChange, readOnly } = props;
    const theme = useTheme();
    const getPuck = useGetPuck();

    const { appState, dispatch, selectedItem } = getPuck();
    const selected = selectedItem as unknown;

    const gridMeta = useMemo(() => {
        if (!isRecord(selected)) return { rows: 4, columns: 6 };
        if (selected.type !== "Grid") return { rows: 4, columns: 6 };
        const props = isRecord(selected.props) ? selected.props : {};
        const rows = typeof props.rows === "number" ? props.rows : 4;
        const columns = typeof props.columns === "number" ? props.columns : 6;
        return { rows, columns };
    }, [selected]);

    const [targetRow, setTargetRow] = useState(0);
    const [targetCol, setTargetCol] = useState(0);

    useEffect(() => {
        const nextRow = Math.min(targetRow, Math.max(0, gridMeta.rows - 1));
        const nextCol = Math.min(targetCol, Math.max(0, gridMeta.columns - 1));

        if (nextRow !== targetRow) setTargetRow(nextRow);
        if (nextCol !== targetCol) setTargetCol(nextCol);
    }, [gridMeta.columns, gridMeta.rows, targetCol, targetRow]);

    const layoutKey = `cell-${targetRow}-${targetCol}`;
    const layouts = value ?? {};
    const current = layouts[layoutKey] ?? {};

    const selectedGridProps = useMemo<Record<string, unknown>>(() => {
        if (!isRecord(selected)) return {};
        if (selected.type !== "Grid") return {};
        return isRecord(selected.props) ? selected.props : {};
    }, [selected]);

    const updateSelectedGridProps = (patch: Record<string, unknown>) => {
        if (!isRecord(selected)) return;
        if (selected.type !== "Grid") return;
        const selectedProps = isRecord(selected.props) ? selected.props : null;
        const selectedId = selectedProps ? selectedProps["id"] : undefined;
        if (typeof selectedId !== "string" || selectedId.trim() === "") return;
        const data = appState?.data;
        if (!data) return;

        type PuckData = NonNullable<typeof data>;
        type ContentItem = PuckData["content"][number];
        type ContentArray = PuckData["content"];
        type Zones = Record<string, ContentArray>;

        const patchItems = (items: ContentArray) => {
            let didChange = false;
            const next = items.map((item) => {
                const raw = item as unknown;
                if (!isRecord(raw)) return item;
                const itemProps = isRecord(raw.props) ? raw.props : null;
                const itemId = itemProps ? itemProps["id"] : undefined;
                if (itemId !== selectedId) return item;
                didChange = true;
                return {
                    ...(raw as ContentItem),
                    props: {
                        ...(itemProps || {}),
                        ...patch,
                    },
                } as ContentItem;
            });
            return { next, didChange };
        };

        let didChangeAny = false;

        const contentResult = patchItems(data.content);
        const nextContent = contentResult.next as ContentArray;
        didChangeAny = didChangeAny || contentResult.didChange;

        const zonesObj: Zones = isRecord(data.zones) ? (data.zones as unknown as Zones) : {};
        let nextZones: Zones = zonesObj;

        for (const [zoneKey, zoneValue] of Object.entries(zonesObj)) {
            if (!Array.isArray(zoneValue)) continue;
            const zoneResult = patchItems(zoneValue as ContentArray);
            if (!zoneResult.didChange) continue;

            if (nextZones === zonesObj) {
                nextZones = { ...zonesObj };
            }
            nextZones[zoneKey] = zoneResult.next as ContentArray;
            didChangeAny = true;
        }

        if (!didChangeAny) return;

        dispatch({
            type: "setData",
            data: {
                ...data,
                content: nextContent,
                zones: nextZones,
            },
        });
    };

    const update = (patch: Partial<GridCellLayout>) => {
        onChange({
            ...layouts,
            [layoutKey]: {
                ...current,
                ...patch,
            },
        });
    };

    return (
        <Box>
            <NativePuckTitleField label="Grid options" subtitle="Applies to the full grid" icon="settings" />

            <Box>
                <FieldLabel label="Columns" el="div">
                    <AutoField
                        field={{
                            type: "select",
                            options: [
                                { label: "1 Column", value: 1 },
                                { label: "2 Columns", value: 2 },
                                { label: "3 Columns", value: 3 },
                                { label: "4 Columns", value: 4 },
                                { label: "5 Columns", value: 5 },
                                { label: "6 Columns", value: 6 },
                            ],
                        }}
                        onChange={(v) => updateSelectedGridProps({ columns: v as number })}
                        value={typeof selectedGridProps.columns === "number" ? selectedGridProps.columns : 1}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Rows" el="div">
                    <AutoField
                        field={{
                            type: "select",
                            options: [
                                { label: "1 Row", value: 1 },
                                { label: "2 Rows", value: 2 },
                                { label: "3 Rows", value: 3 },
                                { label: "4 Rows", value: 4 },
                            ],
                        }}
                        onChange={(v) => updateSelectedGridProps({ rows: v as number })}
                        value={typeof selectedGridProps.rows === "number" ? selectedGridProps.rows : 1}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Equal height cells" el="div">
                    <AutoField
                        field={{
                            type: "radio",
                            options: [
                                { label: "Yes", value: true },
                                { label: "No", value: false },
                            ],
                        }}
                        onChange={(v) => updateSelectedGridProps({ grow: v as boolean })}
                        value={typeof selectedGridProps.grow === "boolean" ? selectedGridProps.grow : false}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Column width distribution" el="div">
                    <AutoField
                        field={{
                            type: "radio",
                            options: [
                                { label: "Equal", value: "equal" },
                                { label: "Content-based", value: "auto" },
                                { label: "Emphasis left", value: "custom" },
                            ],
                        }}
                        onChange={(v) => updateSelectedGridProps({ columnWidth: v as string })}
                        value={
                            typeof selectedGridProps.columnWidth === "string" ? selectedGridProps.columnWidth : "equal"
                        }
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Row gap" el="div">
                    <AutoField
                        field={{ type: "select", options: SPACING_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ rowGap: v as number })}
                        value={
                            typeof selectedGridProps.rowGap === "number" ? selectedGridProps.rowGap
                            : typeof selectedGridProps.spacing === "number" ?
                                selectedGridProps.spacing
                            :   2
                        }
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Column gap" el="div">
                    <AutoField
                        field={{ type: "select", options: SPACING_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ columnGap: v as number })}
                        value={
                            typeof selectedGridProps.columnGap === "number" ? selectedGridProps.columnGap
                            : typeof selectedGridProps.spacing === "number" ?
                                selectedGridProps.spacing
                            :   2
                        }
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Grid background color" el="div">
                    <ColorPickerField
                        label=""
                        value={
                            typeof selectedGridProps.backgroundColor === "string" ?
                                selectedGridProps.backgroundColor
                            :   undefined
                        }
                        onChange={(v) => updateSelectedGridProps({ backgroundColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Grid border color" el="div">
                    <ColorPickerField
                        label=""
                        value={
                            typeof selectedGridProps.borderColor === "string" ?
                                selectedGridProps.borderColor
                            :   undefined
                        }
                        onChange={(v) => updateSelectedGridProps({ borderColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Grid border width" el="div">
                    <AutoField
                        field={{ type: "select", options: BORDER_WIDTH_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ borderWidth: v as number })}
                        value={typeof selectedGridProps.borderWidth === "number" ? selectedGridProps.borderWidth : 0}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Grid border radius" el="div">
                    <AutoField
                        field={{ type: "select", options: RADIUS_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ borderRadius: v as number })}
                        value={typeof selectedGridProps.borderRadius === "number" ? selectedGridProps.borderRadius : 0}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <SpacingSidesField
                sx={{ mt: theme.spacing(2) }}
                label="Grid padding"
                subtitle="Padding applied to the grid container"
                icon="dashboard2"
                centerLabel="Grid"
                value={selectedGridProps.gridPadding as unknown as SpacingSidesValue}
                onChange={(v) => updateSelectedGridProps({ gridPadding: v })}
                readOnly={readOnly}
            />
            <Divider sx={{ my: theme.spacing(2), mx: theme.spacing(-2) }} />

            <NativePuckTitleField
                label="General cell options"
                subtitle="Applies to all cells (unless overridden)"
                icon="settings"
            />

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="General cell background color" el="div">
                    <ColorPickerField
                        label=""
                        value={
                            typeof selectedGridProps.defaultCellBackgroundColor === "string" ?
                                selectedGridProps.defaultCellBackgroundColor
                            :   undefined
                        }
                        onChange={(v) => updateSelectedGridProps({ defaultCellBackgroundColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="General cell border color" el="div">
                    <ColorPickerField
                        label=""
                        value={
                            typeof selectedGridProps.defaultCellBorderColor === "string" ?
                                selectedGridProps.defaultCellBorderColor
                            :   undefined
                        }
                        onChange={(v) => updateSelectedGridProps({ defaultCellBorderColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="General cell border width" el="div">
                    <AutoField
                        field={{ type: "select", options: BORDER_WIDTH_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ defaultCellBorderWidth: v as number })}
                        value={
                            typeof selectedGridProps.defaultCellBorderWidth === "number" ?
                                selectedGridProps.defaultCellBorderWidth
                            :   0
                        }
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="General cell border radius" el="div">
                    <AutoField
                        field={{ type: "select", options: RADIUS_OPTIONS }}
                        onChange={(v) => updateSelectedGridProps({ defaultCellBorderRadius: v as number })}
                        value={
                            typeof selectedGridProps.defaultCellBorderRadius === "number" ?
                                selectedGridProps.defaultCellBorderRadius
                            :   0
                        }
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <SpacingSidesField
                sx={{ mt: theme.spacing(2) }}
                label="General cell padding"
                subtitle="Padding applied to all cells (unless overridden)"
                icon="dashboard2"
                centerLabel="Cell"
                value={selectedGridProps.cellPadding as unknown as SpacingSidesValue}
                onChange={(v) => updateSelectedGridProps({ cellPadding: v })}
                readOnly={readOnly}
            />

            <Divider sx={{ my: theme.spacing(2), mx: theme.spacing(-2) }} />

            <NativePuckTitleField label="Cell options" subtitle="Click a cell to configure it" icon="settings" />

            <Box sx={{ mt: theme.spacing(2) }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${Math.max(1, gridMeta.columns)}, 1fr)`,
                        gap: theme.spacing(0.25),
                        width: "100%",
                        maxWidth: 320,
                    }}
                >
                    {Array.from({ length: Math.max(1, gridMeta.rows) }).map((_, r) =>
                        Array.from({ length: Math.max(1, gridMeta.columns) }).map((_, c) => {
                            const id = `cell-${r}-${c}`;
                            const cell = layouts[id] ?? {};
                            const isActive = r === targetRow && c === targetCol;
                            const hasOverride = Object.keys(cell).length > 0;
                            const cellBorderWidth = typeof cell.borderWidth === "number" ? cell.borderWidth : 0;
                            const cellBorderColor =
                                typeof cell.borderColor === "string" && cell.borderColor.trim() !== "" ?
                                    cell.borderColor
                                :   theme.palette.divider;
                            const previewBorderWidth = cellBorderWidth > 0 ? Math.min(2, cellBorderWidth) : 1;
                            const previewBorderColor = cellBorderWidth > 0 ? cellBorderColor : theme.palette.divider;
                            return (
                                <Box
                                    key={id}
                                    onClick={() => {
                                        if (readOnly) return;
                                        setTargetRow(r);
                                        setTargetCol(c);
                                    }}
                                    sx={{
                                        height: 20,
                                        borderRadius: theme.spacing(0.5),
                                        border: `${previewBorderWidth}px solid ${previewBorderColor}`,
                                        outline:
                                            isActive ?
                                                `2px solid ${theme.palette.primary.main}`
                                            :   "2px solid transparent",
                                        outlineOffset: 0,
                                        backgroundColor: cell.backgroundColor || theme.palette.common.white,
                                        cursor: readOnly ? "default" : "pointer",
                                        position: "relative",
                                    }}
                                >
                                    {hasOverride && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                right: 2,
                                                top: 2,
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                backgroundColor: theme.palette.primary.main,
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        }),
                    )}
                </Box>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Cell background color" el="div">
                    <ColorPickerField
                        label=""
                        value={current.backgroundColor as string | undefined}
                        onChange={(v) => update({ backgroundColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Cell border color" el="div">
                    <ColorPickerField
                        label=""
                        value={current.borderColor as string | undefined}
                        onChange={(v) => update({ borderColor: v || undefined })}
                        allowClear
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Cell border width" el="div">
                    <AutoField
                        field={{ type: "select", options: BORDER_WIDTH_OPTIONS }}
                        onChange={(v) => update({ borderWidth: v as number })}
                        value={typeof current.borderWidth === "number" ? current.borderWidth : 0}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ mt: theme.spacing(2) }}>
                <FieldLabel label="Cell border radius" el="div">
                    <AutoField
                        field={{ type: "select", options: RADIUS_OPTIONS }}
                        onChange={(v) => update({ borderRadius: v as number })}
                        value={typeof current.borderRadius === "number" ? current.borderRadius : 0}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <SpacingSidesField
                sx={{ mt: theme.spacing(2) }}
                label="Cell padding"
                subtitle="Overrides default cell padding for this cell"
                icon="dashboard2"
                centerLabel="Cell"
                value={current.padding}
                onChange={(v) => update({ padding: v })}
                readOnly={readOnly}
            />
        </Box>
    );
};

export default GridCellLayoutField;
